import { VueConstructor } from 'vue';
import { Store } from 'vuex';
import { BUS, EventBus } from '../bus';
import { EVENTS } from '../bus/events.const';
import { Components } from '../components';
import { COMPONENTS } from '../components/components.registry';
import { container } from '../container';
import { DEPENDENCIES } from '../container/dependencies.const';
import { FILTERS_REGISTRY } from '../filters';
import { CurrencyOptions, Messages } from '../i18n';
import { logger } from '../logger';
import { SLOTS } from '../models';
import { GETTERS_KEYS, RootStoreState } from '../store';
import { Dictionary } from '../utils';

declare module 'vue/types/vue' {
  interface Vue {
    $x: {
      currency: CurrencyOptions;
      emit: EventBus['emit'];
      events: typeof EVENTS;
      messages: Messages;
      on: EventBus['on'];
      slots: typeof SLOTS;
      smart: Record<keyof typeof Components.Smart, Vue | undefined>
    };
  }
}

export class EmpathyXPlugin {

  private readonly logger = logger.child('[Plugin]');
  private GlobalVue: VueConstructor;

  install(GlobalVue: VueConstructor) {
    this.GlobalVue = GlobalVue;
    this.modifyPrototype();
    this.registerFilters();
    this.registerComponents();
    GlobalVue.config.errorHandler = (info, err, vm) => logger.error(err, vm, info);
  }

  private modifyPrototype(): void {
    const store = container.get<Store<RootStoreState>>(DEPENDENCIES.Store);
    this.GlobalVue.prototype.$x = {
      on: BUS.on,
      emit: BUS.emit,
      events: EVENTS,
      slots: SLOTS,
      smart: {},
      get messages() { return store.getters[GETTERS_KEYS.config.messages]; },
      get currency() { return store.getters[GETTERS_KEYS.config.currency]; }
    };
  }

  private registerFilters(): void {
    const filtersRegisteredLog = Object.entries(FILTERS_REGISTRY)
      .reduce<Dictionary<any>>((log, [name, filter]) => {
        this.GlobalVue.filter(name, filter);
        log[name] = filter;
        return log;
      }, {});
    this.logger.info('Registered filters:', filtersRegisteredLog);
  }

  private registerComponents(): void {
    const registeredPureComponentsLog = this.registerComponentsGroup(COMPONENTS.PURE, Components.Pure);
    this.logger.info('Registered pure components:', registeredPureComponentsLog);
    const registeredPanelComponentsLog = this.registerComponentsGroup(COMPONENTS.PANELS, Components.Panels);
    this.logger.info('Registered panel components:', registeredPanelComponentsLog);
    const registeredSmartComponentsLog = this.registerComponentsGroup(COMPONENTS.SMART, Components.Smart);
    this.logger.info('Registered smart components:', registeredSmartComponentsLog);
    const registeredTemplatesLog = this.registerComponentsGroup(COMPONENTS.TEMPLATES, Components.Templates);
    this.logger.info('Registered templates:', registeredTemplatesLog);
  }

  private registerComponentsGroup(prefixedComponentNames: Dictionary<string>, components: Dictionary<VueConstructor | {}>)
    : Dictionary<any> {
    return Object.entries(components).reduce<Dictionary<any>>((log, [name, component]) => {
      const prefixedName = prefixedComponentNames[name];
      if (prefixedName) {
        this.GlobalVue.component(prefixedName, component);
        log[prefixedName] = component;
      } else {
        logger.error(`Component '${ name }' is not found`);
      }
      return log;
    }, {});
  }
}
